import { FileModel, FileUsageEntry, FileUsageModel } from '@/typings/api/files';
import { zIndexAboveDrawers } from '@/utils/dom';
import { F, getIntlTranslation } from '@/utils/intl';
import { Modal, Typography, message } from 'antd';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'umi';

import styles from './FileGrid.less';

/** One line of "what would break": the thing, and what kind of thing it is. */
const UsageLine: React.FC<{ usage: FileUsageEntry }> = ({ usage }) => {
  const name = usage.name || String(usage.id);

  if (usage.type === 'email_template') {
    return (
      <F
        id="components.fileLibrary.usage.template"
        defaultMessage="The email “{name}”"
        values={{ name }}
      />
    );
  }

  if (usage.type === 'template_fragment') {
    return (
      <F
        id="components.fileLibrary.usage.fragment"
        defaultMessage="The building block “{name}”"
        values={{ name }}
      />
    );
  }

  if (usage.type === 'journey_theme') {
    return (
      <F
        id="components.fileLibrary.usage.theme"
        defaultMessage="The email theme of “{name}”"
        values={{ name }}
      />
    );
  }

  return (
    <F
      id="components.fileLibrary.usage.image"
      defaultMessage="The image library"
    />
  );
};

const UsageList: React.FC<{ usages: FileUsageEntry[] }> = ({ usages }) => (
  <ul className={styles.usageList}>
    {usages.map((usage, index) => (
      <li key={index}>
        <UsageLine usage={usage} />
      </li>
    ))}
  </ul>
);

/**
 * Deleting a file, with the "what would break" check in front of it.
 *
 * Unlike a fragment, a file has no standard version to fall back on: the bytes
 * leave the bucket and every email already referencing them renders a broken
 * image from then on — including ones that have already been sent and are
 * sitting in an inbox. So being in use never blocks outright (someone has to be
 * able to clear out a file they know is stale), but it is always shown first,
 * and confirming it is what sends `force`.
 *
 * The server runs the same check on the DELETE, so this is about telling you
 * *before* you press it, not about being the guard.
 *
 * The dialog comes from `Modal.useModal()` rather than the static
 * `Modal.confirm`. The static call renders into a React root of its own,
 * outside this app's providers, and the content below is not context-free: `F`
 * resolves its text through `useIntl()`, which needs the `IntlProvider` in the
 * ancestry. The hook's `contextHolder` renders inside the tree, so the dialog
 * gets the locale, the theme and antd's own stacking — the caller only has to
 * mount it.
 */
export function useFileDeletion({
  loadUsage,
  onConfirmed,
}: {
  loadUsage: (file: FileModel) => Promise<FileUsageModel>;
  onConfirmed: (file: FileModel, force: boolean) => Promise<boolean> | void;
}) {
  const intl = useIntl();
  const [modal, contextHolder] = Modal.useModal();
  // One id, not a boolean: cards share the hook, and only the one acted on
  // should look busy.
  const [busyId, setBusyId] = useState<string | null>(null);

  const requestDelete = useCallback(
    async (file: FileModel) => {
      let usage: FileUsageModel;
      try {
        setBusyId(file.id);
        usage = await loadUsage(file);
      } catch (error) {
        // Nothing below runs, and the click has to say so. The caller fires
        // this from an event handler and cannot await it, so an exception here
        // would otherwise be an unhandled rejection and a button that visibly
        // does nothing — which is indistinguishable from an unwired button.
        message.error(
          getIntlTranslation(intl, {
            id: 'components.fileLibrary.delete.checkFailed',
            defaultMessage:
              'Could not check what uses this file, so it was not deleted. Please try again.',
          }),
        );
        throw error;
      } finally {
        setBusyId(null);
      }

      // Or the dialog opens behind the drawer that raised it.
      const zIndex = zIndexAboveDrawers();

      modal.confirm({
        zIndex,
        okButtonProps: { danger: true },
        okText: getIntlTranslation(intl, {
          id: 'common.actions.delete',
          defaultMessage: 'Delete',
        }),
        cancelText: getIntlTranslation(intl, {
          id: 'common.actions.cancel',
          defaultMessage: 'Cancel',
        }),
        title: getIntlTranslation(intl, {
          id: 'components.fileLibrary.delete.title',
          defaultMessage: 'Delete this file?',
        }),
        content: (
          <div>
            <Typography.Paragraph>
              {usage.in_use ? (
                <F
                  id="components.fileLibrary.delete.usedHint"
                  defaultMessage="These still use the file. Deleting it cannot be undone, and they will show a broken image from then on:"
                />
              ) : (
                <F
                  id="components.fileLibrary.delete.unusedHint"
                  defaultMessage="Nothing references this file. Deleting it cannot be undone."
                />
              )}
            </Typography.Paragraph>
            {usage.in_use && <UsageList usages={usage.usages} />}
          </div>
        ),
        // Returning the promise keeps the dialog up while the delete runs and
        // closes it on success. A rejection has to be reported: antd only
        // leaves the dialog open, which reads as the button doing nothing.
        onOk: async () => {
          try {
            return await onConfirmed(file, usage.in_use);
          } catch (error) {
            message.error(
              getIntlTranslation(intl, {
                id: 'components.fileLibrary.delete.failed',
                defaultMessage: 'The file could not be deleted.',
              }),
            );
            throw error;
          }
        },
      });
    },
    [intl, modal, loadUsage, onConfirmed],
  );

  return { requestDelete, busyId, contextHolder };
}
